/**
 * Keyboard navigation for the branded `Select` — pure, DOM-free, so the
 * arrow/Home/End/type-ahead rules are testable without a browser and the
 * component stays a thin shell around them.
 *
 * Every helper takes the option list and an index (`-1` = nothing active)
 * and returns an index (`-1` = nothing to land on). Disabled rows are never
 * returned: they are visible but not pickable, so the keyboard walks past them.
 */
export interface NavOption {
  label: string;
  disabled?: boolean;
}

const pickable = (o: NavOption | undefined): boolean => !!o && !o.disabled;

export function firstEnabled(options: NavOption[]): number {
  return options.findIndex(pickable);
}

export function lastEnabled(options: NavOption[]): number {
  for (let i = options.length - 1; i >= 0; i--) if (pickable(options[i])) return i;
  return -1;
}

function walk(options: NavOption[], from: number, dir: 1 | -1, wrap: boolean): number {
  const n = options.length;
  for (let step = 1; step <= n; step++) {
    let i = from + dir * step;
    if (i < 0 || i >= n) {
      if (!wrap) break;
      i = ((i % n) + n) % n;
    }
    if (i === from) break;
    if (pickable(options[i])) return i;
  }
  return -1;
}

/**
 * The next pickable row in `dir` from `from`. Without `wrap` the ends are
 * sticky (the row you are on stays active), so holding ↓ never jumps back to
 * the top by surprise. From `-1` — or from an index the list no longer has
 * (a track list that reloaded while open) — it enters at the matching end.
 * From a row that is itself disabled (it became so under the cursor) and has
 * nothing further in `dir`, it falls back the other way: the keyboard must
 * never go dead on a row it cannot pick.
 */
export function nextEnabledIndex(options: NavOption[], from: number, dir: 1 | -1, wrap = false): number {
  if (from < 0 || from >= options.length) return dir === 1 ? firstEnabled(options) : lastEnabled(options);
  const ahead = walk(options, from, dir, wrap);
  if (ahead >= 0) return ahead;
  if (pickable(options[from])) return from;
  return walk(options, from, dir === 1 ? -1 : 1, false);
}

/** Lower-cased, diacritics stripped ("Français" → "francais"), so a French
 *  keyboard and a US one find the same row. */
function fold(s: string): string {
  return s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

/**
 * Type-ahead: the first row whose label starts with `buffer`, searched from
 * the current row (inclusive — typing "fr" while on "French" stays there)
 * and wrapping round. A buffer that is one letter repeated ("ff") is the
 * listbox convention for "next row starting with f": it cycles from the row
 * AFTER the current one, so tapping a key walks through the same initial.
 * `-1` when nothing pickable matches (an all-disabled list included).
 */
export function typeAheadIndex(options: NavOption[], from: number, buffer: string): number {
  const raw = fold(buffer.trim());
  if (!raw) return -1;
  const repeated = raw.length > 1 && raw.split("").every((ch) => ch === raw[0]);
  const needle = repeated ? raw[0] : raw;
  const n = options.length;
  const start = repeated || from < 0 ? from + 1 : from;
  for (let step = 0; step < n; step++) {
    const i = (((start + step) % n) + n) % n;
    const o = options[i];
    if (pickable(o) && fold(o.label).startsWith(needle)) return i;
  }
  return -1;
}
